import { orderBy, partition, range, uniqBy } from "lodash-es";
import { Duration } from "luxon";
import { FieldError, FieldErrors, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { PickUpSettingsSchema } from "Pages/OnlinePickUpTimes/schema";

import { ErrorBox, SelectInput } from "@online_ordering/Atoms";

import { Table } from "@online_ordering/schema";

import { useVenue } from "../../VenueContext";
import { useFetchPickUpSettings } from "../../queries";
import { SimpleOrderFormData } from "../Checkout/SimpleOrderCart";
import { CustomerTab } from "../CustomerTab/schema";
import {
  useLastPickUpTime,
  useOnlineOrderingAvailableNow,
} from "../OnlineOrderingSchedule/OnlineOrderingDisabled";

export const PickUpOptions = ({
  isEditable,
  register,
  recordChange,
}: {
  isEditable: boolean;
  recordChange?: () => void;
  register: UseFormReturn<SimpleOrderFormData>["register"];
}) => {
  const { t } = useTranslation("onsite");
  return (
    <div className="mx-auto flex rounded-full border border-venue-primary p-2 text-center text-xl font-bold md:w-2/3">
      <div className="group flex-1">
        <input
          className="peer hidden"
          type="radio"
          value="takeaway"
          id="pickUp[pickUp]"
          {...register("pickUpMethod", {
            onChange: recordChange ? recordChange : undefined,
          })}
          disabled={!isEditable}
        />
        <label
          htmlFor="pickUp[pickUp]"
          className="block cursor-pointer rounded-l-full px-12 py-8 peer-checked:rounded-full peer-checked:bg-venue-primary peer-checked:text-venue-secondary"
        >
          {t("cart.pickUp")}
        </label>
      </div>
      <div className="group flex-1">
        <input
          type="radio"
          id="pickUp[eatIn]"
          value="have_here"
          className="peer hidden"
          {...register("pickUpMethod", {
            onChange: recordChange ? recordChange : undefined,
          })}
          disabled={!isEditable}
        />
        <label
          htmlFor="pickUp[eatIn]"
          className="peer block cursor-pointer rounded-r-full px-12 py-8 peer-checked:rounded-full peer-checked:bg-venue-primary peer-checked:text-venue-secondary"
        >
          {t("cart.eatIn")}
        </label>
      </div>
    </div>
  );
};

export const TabDetails = ({ customerTab }: { customerTab: CustomerTab }) => {
  const { t } = useTranslation("onsite");

  return (
    <>
      {customerTab.tableName && (
        <div className="block space-y-8 text-xl font-bold">
          <div>{t("cart.tableNumber")}</div>
          <div className="block w-full rounded-venue-brand bg-venue-tertiary p-12 shadow-venue-brand-style disabled:cursor-not-allowed">
            {customerTab.tableName}
          </div>
        </div>
      )}
      {customerTab.name && (
        <div className="block space-y-8 text-xl font-bold">
          <div>{t("cart.tabName")}</div>
          <div className="block w-full rounded-venue-brand bg-venue-tertiary p-12 shadow-venue-brand-style disabled:cursor-not-allowed">
            {customerTab.name}
          </div>
        </div>
      )}
    </>
  );
};
export const FormErrors = ({
  errors,
}: {
  errors: Record<string, FieldError>;
}) => {
  if (!errors || Object.keys(errors).length == 0) return null;
  return (
    <ErrorBox>
      {Object.entries(errors).map(([key, value]) => (
        <div key={key}>
          {key} {value.message}
        </div>
      ))}
    </ErrorBox>
  );
};
const ErrorMessage = ({ message }: { message: string }) => {
  return (
    <div className="text-base font-normal text-venue-highlight">{message}</div>
  );
};
const getHumanDuration = (minutes: number) =>
  Duration.fromObject({
    ...(Math.floor(minutes / 60) != 0
      ? { hours: Math.floor(minutes / 60) }
      : {}),
    ...(Math.floor(minutes % 60) != 0 ? { minutes: minutes % 60 } : {}),
  }).toHuman();

const naturalTableSort = (tables: Table[]) => {
  // We want to ensure that tables are sorted like , 1,2,...,10,11,12, SomeString, SomeOtherString
  // and not 1,10,11,12,2,3, SomeString, SomeOtherString
  // We can achieve this by sorting tables that start with a number first
  const [startsWithNumber, rest] = partition(tables, (t) => /^\d/.test(t.name));
  return [
    ...orderBy(startsWithNumber, (t) => parseInt(t.name)),
    ...orderBy(rest, "name"),
  ];
};
export const CartDetailsForm = ({
  register,
  isEditable,
  isHaveHere,
  onChange,
  errors,
}: {
  register: UseFormReturn<SimpleOrderFormData>["register"];
  isEditable: boolean;
  isHaveHere: boolean;
  errors: FieldErrors<SimpleOrderFormData>;
  onChange: () => void;
}) => {
  const { t } = useTranslation("onsite");
  const tables = naturalTableSort(useVenue().tables);
  const layoutCount = uniqBy(tables, (t) => t.floorLayoutUuid).length;
  const pickUpSettingsQuery = useFetchPickUpSettings();
  const onlineOrderingAvailable = useOnlineOrderingAvailableNow();
  const lastPickUpTime = useLastPickUpTime();
  if (pickUpSettingsQuery.isLoading) return <>Loading...</>;
  const pickUpSettings = pickUpSettingsQuery.isSuccess
    ? pickUpSettingsQuery.data
    : PickUpSettingsSchema.parse({});

  const pickUpInMinutes = range(
    pickUpSettings.noSoonerThan,
    (lastPickUpTime || pickUpSettings.noLaterThan) + 1,
    pickUpSettings.incrementsOf,
  );
  const showPickUpTime = pickUpSettings.status == "enabled" && !isHaveHere;

  const validPickUpTimes =
    pickUpInMinutes.length > 0 && onlineOrderingAvailable;
  return (
    <>
      {isHaveHere && (
        <>
          <label className="block space-y-8 text-xl font-bold">
            <div>{t("cart.tableNumber")}</div>
            <SelectInput
              {...register("floorTableId", {
                onBlur: onChange,
                shouldUnregister: true,
              })}
              disabled={!isEditable}
            >
              <option key={"null"}> </option>
              {tables.map((table) => (
                <option key={table.uuid} value={table.uuid}>
                  {layoutCount > 1
                    ? t("cart.tableNameWithLayout", {
                        name: table.name,
                        layoutName: table.floorLayoutName,
                      })
                    : t("cart.tableName", {
                        name: table.name,
                        layoutName: table.floorLayoutName,
                      })}
                </option>
              ))}
            </SelectInput>

            {errors.floorTableId && (
              <ErrorMessage message={t("cart.tableNumber_error")} />
            )}
          </label>
        </>
      )}
      {showPickUpTime && (
        <>
          <label className="block space-y-8 text-xl font-bold">
            <div>{t("cart.pickUpIn")}</div>
            <SelectInput
              {...register("pickUpInMinutes", {
                shouldUnregister: true,
              })}
              onBlur={onChange}
              disabled={!isEditable || !validPickUpTimes}
            >
              {validPickUpTimes ? (
                <>
                  {pickUpSettings.allowAsap && (
                    <option key={null}>{t("cart.asap")}</option>
                  )}
                  {pickUpInMinutes.map((minute) => (
                    <option key={minute} value={minute}>
                      {getHumanDuration(minute)}
                    </option>
                  ))}
                </>
              ) : (
                <option key={null}>{t("cart.noValidPickUpTimes")}</option>
              )}
            </SelectInput>
            {errors.pickUpInMinutes?.message && (
              <ErrorMessage message={errors.pickUpInMinutes.message} />
            )}
          </label>
        </>
      )}
      <label className="block space-y-8 text-xl font-bold">
        <div>{t("cart.yourName")}</div>
        <input
          {...register("name", { onBlur: onChange })}
          className="block w-full rounded-venue-brand bg-venue-tertiary p-12 shadow-venue-brand-style disabled:cursor-not-allowed"
          disabled={!isEditable}
        />
        {errors.name && (
          <div className="text-base font-normal text-venue-highlight">
            {t("cart.yourName_error")}
          </div>
        )}
      </label>
    </>
  );
};
